import React, { useEffect, useState } from 'react';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
import { Link } from 'react-router-dom';
import { UserIcon, CreditCardIcon, BellIcon, ShieldIcon, SaveIcon, CheckIcon, SunIcon, MoonIcon, PaletteIcon, XIcon, AlertCircleIcon } from 'lucide-react';
import { Toggle } from '../Toggle';
import { DeleteAccountModal } from '../DeleteAccountModal';
import { useAuth } from '../../contexts/AuthContext';
import { Toast } from '../Toast';
export const StudentSettings = () => {
  const {
    currentUser
  } = useAuth();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    // Check localStorage for saved preference, default to dark mode
    const savedMode = localStorage.getItem('studentDashboardTheme');
    return savedMode === 'dark';
  });
  // Form states for Contact Information
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  // Backup values for cancel functionality
  const [originalValues, setOriginalValues] = useState({
    name: '',
    email: '',
    phone: ''
  });
  // Track which sections have unsaved changes
  const [dirtyFields, setDirtyFields] = useState({
    contact: false,
    notifications: false
  });
  // Notification preferences
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [originalEmailNotifications, setOriginalEmailNotifications] = useState(true);
  const [smsNotifications, setSmsNotifications] = useState(false);
  const [originalSmsNotifications, setOriginalSmsNotifications] = useState(false);
  const [marketingEmails, setMarketingEmails] = useState(true);
  const [originalMarketingEmails, setOriginalMarketingEmails] = useState(true);
  // Form submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState({
    visible: false,
    message: '',
    type: 'success' as 'success' | 'error' | 'info' | 'warning'
  });
  // Delete account modal state
  const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] = useState(false);
  // Editing states
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  // Check if any field is being edited
  const isEditing = isEditingName || isEditingEmail || isEditingPhone;
  // Toggle sidebar on mobile
  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };
  // Load user data
  useEffect(() => {
    if (currentUser) {
      const displayName = currentUser.displayName || '';
      const emailAddress = currentUser.email || '';
      setName(displayName);
      setEmail(emailAddress);
      // Store original values for cancel functionality
      setOriginalValues({
        name: displayName,
        email: emailAddress,
        phone: ''
      });
      // Store original notification values
      setOriginalEmailNotifications(emailNotifications);
      setOriginalSmsNotifications(smsNotifications);
      setOriginalMarketingEmails(marketingEmails);
      // You could fetch additional user data from Firestore here
      // For example:
      // const fetchUserData = async () => {
      //   try {
      //     const userDoc = await getDoc(doc(db, 'students', currentUser.uid))
      //     if (userDoc.exists()) {
      //       const userData = userDoc.data()
      //       setPhone(userData.phone || '')
      //       setOriginalValues(prev => ({...prev, phone: userData.phone || ''}))
      //     }
      //   } catch (error) {
      //     console.error('Error fetching user data:', error)
      //   }
      // }
      // fetchUserData()
    }
  }, [currentUser]);
  // Check for unsaved changes in contact information
  useEffect(() => {
    const hasContactChanges = name !== originalValues.name || email !== originalValues.email || phone !== originalValues.phone;
    setDirtyFields(prev => ({
      ...prev,
      contact: hasContactChanges
    }));
  }, [name, email, phone, originalValues]);
  // Check for unsaved changes in notification settings
  useEffect(() => {
    const hasNotificationChanges = emailNotifications !== originalEmailNotifications || smsNotifications !== originalSmsNotifications || marketingEmails !== originalMarketingEmails;
    setDirtyFields(prev => ({
      ...prev,
      notifications: hasNotificationChanges
    }));
  }, [emailNotifications, smsNotifications, marketingEmails, originalEmailNotifications, originalSmsNotifications, originalMarketingEmails]);
  // Listen for theme changes from the Navbar component
  useEffect(() => {
    const handleThemeChange = event => {
      setDarkMode(event.detail.darkMode);
    };
    window.addEventListener('themeChange', handleThemeChange);
    return () => {
      window.removeEventListener('themeChange', handleThemeChange);
    };
  }, []);
  // Start editing a field
  const startEditing = field => {
    if (field === 'name') setIsEditingName(true);
    if (field === 'email') setIsEditingEmail(true);
    if (field === 'phone') setIsEditingPhone(true);
  };
  // Cancel all edits
  const cancelEdits = () => {
    // Reset form values to original values
    setName(originalValues.name);
    setEmail(originalValues.email);
    setPhone(originalValues.phone);
    // Reset editing states
    setIsEditingName(false);
    setIsEditingEmail(false);
    setIsEditingPhone(false);
  };
  // Handle form submission
  const handleSubmit = e => {
    if (e) e.preventDefault();
    setIsSubmitting(true);
    setError('');
    // Simulate API call
    setTimeout(() => {
      // Success
      setToast({
        visible: true,
        message: 'Your information has been updated successfully!',
        type: 'success'
      });
      setIsSubmitting(false);
      // Update original values
      setOriginalValues({
        name,
        email,
        phone
      });
      // Update original notification values
      setOriginalEmailNotifications(emailNotifications);
      setOriginalSmsNotifications(smsNotifications);
      setOriginalMarketingEmails(marketingEmails);
      // Reset all editing states
      setIsEditingName(false);
      setIsEditingEmail(false);
      setIsEditingPhone(false);
      // Reset dirty fields
      setDirtyFields({
        contact: false,
        notifications: false
      });
    }, 1500);
  };
  // Handle notification settings submission
  const handleNotificationSubmit = () => {
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      // Success
      setToast({
        visible: true,
        message: 'Notification preferences saved successfully!',
        type: 'success'
      });
      setIsSubmitting(false);
      // Update original notification values
      setOriginalEmailNotifications(emailNotifications);
      setOriginalSmsNotifications(smsNotifications);
      setOriginalMarketingEmails(marketingEmails);
      // Reset dirty field
      setDirtyFields(prev => ({
        ...prev,
        notifications: false
      }));
    }, 1500);
  };
  // Close toast
  const handleCloseToast = () => {
    setToast(prev => ({
      ...prev,
      visible: false
    }));
  };
  // Theme classes based on dark mode
  const themeClasses = {
    background: darkMode ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]' : 'bg-gray-50',
    text: darkMode ? 'text-white' : 'text-gray-800',
    subtext: darkMode ? 'text-gray-300' : 'text-gray-700',
    card: darkMode ? 'bg-navy-800/80' : 'bg-white shadow-md',
    input: darkMode ? 'bg-navy-900/50 border-gray-700 text-white' : 'bg-gray-50 border-gray-400 text-gray-800',
    inputActive: darkMode ? 'bg-navy-900/50 border-deep-pink text-white ring-1 ring-deep-pink' : 'bg-white border-deep-pink text-gray-800 ring-1 ring-deep-pink',
    border: darkMode ? 'border-gray-700' : 'border-gray-300',
    buttonPrimary: darkMode ? 'bg-deep-pink hover:bg-burgundy text-white' : 'bg-deep-pink hover:bg-burgundy text-white',
    buttonSecondary: darkMode ? 'bg-navy-900 hover:bg-navy-800 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800',
    toast: darkMode ? 'bg-green-800/90 text-white border border-green-700' : 'bg-green-100 text-green-800 border border-green-200'
  };
  return <div className={`w-full min-h-screen ${themeClasses.background} ${themeClasses.text} font-sans`} data-id="element-8059">
      <Navbar setIsDeleteAccountModalOpen={() => {}} data-id="element-8060" />
      {/* Toast Notification */}
      {toast.visible && <div className="fixed top-24 right-4 z-50 max-w-md" data-id="element-8061">
          <Toast message={toast.message} type={toast.type} duration={5000} onClose={handleCloseToast} data-id="element-8062" />
        </div>}
      {/* Mobile sidebar toggle button - improved touch target */}
      <div className="fixed bottom-4 right-4 z-50 md:hidden" data-id="element-8063">
        <button onClick={toggleMobileSidebar} className="bg-deep-pink text-white p-4 rounded-full shadow-lg flex items-center justify-center" aria-label="Toggle settings menu" style={{
        minWidth: '56px',
        minHeight: '56px'
      }} data-id="element-8064">
          {isMobileSidebarOpen ? <XIcon className="h-6 w-6" data-id="element-8065" /> : <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-8066">
              <line x1="3" y1="12" x2="21" y2="12" data-id="element-8067"></line>
              <line x1="3" y1="6" x2="21" y2="6" data-id="element-8068"></line>
              <line x1="3" y1="18" x2="21" y2="18" data-id="element-8069"></line>
            </svg>}
        </button>
      </div>
      <main className="pt-20 pb-16" data-id="element-8070">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-8071">
          <div className="flex flex-col md:flex-row gap-6" data-id="element-8072">
            {/* Sidebar container - improved mobile experience */}
            <div className={`md:block ${isMobileSidebarOpen ? 'fixed inset-0 z-40 bg-black/50 backdrop-blur-sm md:relative md:bg-transparent md:backdrop-blur-none' : 'hidden md:block'}`} data-id="element-8073">
              <div className="md:fixed md:w-64 w-[85%] max-w-xs h-full overflow-auto" style={{
              top: '80px'
            }} data-id="element-8074">
                <div className={`${darkMode ? 'bg-navy-900/90' : 'bg-white shadow-md'} p-4 rounded-xl w-full h-full`} data-id="element-8075">
                  <nav data-id="element-8076">
                    <ul className="space-y-1" data-id="element-8077">
                      <li data-id="element-8078">
                        <a href="#contact" className={`flex items-center px-4 py-4 ${darkMode ? 'text-deep-pink bg-navy-800/50' : 'text-deep-pink bg-red-50'} rounded-lg font-medium relative`} onClick={() => window.innerWidth < 768 && toggleMobileSidebar()} data-id="element-8079">
                          <UserIcon className="h-6 w-6 mr-3" data-id="element-8080" />
                          Contact Information
                          {dirtyFields.contact && <span className="absolute top-4 right-4 h-2 w-2 rounded-full bg-deep-pink" data-id="element-8081"></span>}
                        </a>
                      </li>
                      <li data-id="element-8082">
                        <a href="#notifications" className={`flex items-center px-4 py-4 ${darkMode ? 'text-white hover:bg-navy-800/70' : 'text-gray-700 hover:bg-gray-100'} rounded-lg font-medium transition-colors relative`} onClick={() => window.innerWidth < 768 && toggleMobileSidebar()} data-id="element-8083">
                          <BellIcon className="h-6 w-6 mr-3" data-id="element-8084" />
                          Notifications
                          {dirtyFields.notifications && <span className="absolute top-4 right-4 h-2 w-2 rounded-full bg-deep-pink" data-id="element-8085"></span>}
                        </a>
                      </li>
                      <li data-id="element-8086">
                        <a href="#security" className={`flex items-center px-4 py-4 ${darkMode ? 'text-white hover:bg-navy-800/70' : 'text-gray-700 hover:bg-gray-100'} rounded-lg font-medium transition-colors`} onClick={() => window.innerWidth < 768 && toggleMobileSidebar()} data-id="element-8087">
                          <ShieldIcon className="h-6 w-6 mr-3" data-id="element-8088" />
                          Security
                        </a>
                      </li>
                      <li data-id="element-8089">
                        <a href="#preferences" className={`flex items-center px-4 py-4 ${darkMode ? 'text-white hover:bg-navy-800/70' : 'text-gray-700 hover:bg-gray-100'} rounded-lg font-medium transition-colors`} onClick={() => window.innerWidth < 768 && toggleMobileSidebar()} data-id="element-8090">
                          <PaletteIcon className="h-6 w-6 mr-3" data-id="element-8091" />
                          Preferences
                        </a>
                      </li>
                    </ul>
                    <div className="mt-6 pt-6 border-t border-gray-700/30" data-id="element-8092">
                      <div className="flex items-center justify-between px-4 py-2" data-id="element-8093">
                        <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-8094">
                          Dark Mode
                        </span>
                        <button onClick={() => {
                        const newMode = !darkMode;
                        setDarkMode(newMode);
                        localStorage.setItem('studentDashboardTheme', newMode ? 'dark' : 'light');
                        // Dispatch event for other components
                        window.dispatchEvent(new CustomEvent('themeChange', {
                          detail: {
                            darkMode: newMode
                          }
                        }));
                      }} className={`p-3 rounded-lg ${darkMode ? 'bg-navy-800 text-yellow-400' : 'bg-gray-200 text-gray-700'}`} style={{
                        minWidth: '48px',
                        minHeight: '48px'
                      }} data-id="element-8095">
                          {darkMode ? <SunIcon className="h-5 w-5" data-id="element-8096" /> : <MoonIcon className="h-5 w-5" data-id="element-8097" />}
                        </button>
                      </div>
                    </div>
                  </nav>
                </div>
                {/* Close button for mobile */}
                {isMobileSidebarOpen && <button className="absolute top-4 right-4 md:hidden text-white bg-navy-800 p-3 rounded-lg" onClick={toggleMobileSidebar} style={{
                minWidth: '48px',
                minHeight: '48px'
              }} data-id="element-8098">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-8099">
                      <line x1="18" y1="6" x2="6" y2="18" data-id="element-8100"></line>
                      <line x1="6" y1="6" x2="18" y2="18" data-id="element-8101"></line>
                    </svg>
                  </button>}
              </div>
            </div>
            {/* Main Content - Add left margin to account for fixed sidebar */}
            <div className="flex-1 md:ml-64" data-id="element-8102">
              {/* Contact Information Section - Mobile optimized form */}
              <section id="contact" className="mb-10" data-id="element-8103">
                <div className={`${themeClasses.card} backdrop-blur-sm rounded-xl p-6`} data-id="element-8104">
                  <h2 className={`text-xl font-serif font-bold ${themeClasses.text} mb-6 border-b ${themeClasses.border} pb-2 flex items-center justify-between`} data-id="element-8105">
                    <div className="flex items-center" data-id="element-8106">
                      <UserIcon className="h-6 w-6 mr-2 text-deep-pink" data-id="element-8107" />
                      Contact Information
                    </div>
                    {dirtyFields.contact && <span className="h-2 w-2 rounded-full bg-deep-pink" data-id="element-8108"></span>}
                  </h2>
                  {error && <div className="mb-6 bg-red-500/20 border border-red-500/30 text-white px-4 py-3 rounded-lg flex items-center" data-id="element-8109">
                      <AlertCircleIcon className="h-5 w-5 mr-2 text-red-400" data-id="element-8110" />
                      {error}
                    </div>}
                  <form onSubmit={handleSubmit} data-id="element-8111">
                    <div className="grid grid-cols-1 gap-6" data-id="element-8112">
                      {/* Full Name */}
                      <div data-id="element-8113">
                        <label htmlFor="name" className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-8114">
                          Full Name
                        </label>
                        <div className="relative" onClick={() => !isEditingName && startEditing('name')} data-id="element-8115">
                          {isEditingName ? <input type="text" id="name" value={name} onChange={e => setName(e.target.value)} className={`w-full px-4 py-4 border ${themeClasses.inputActive} rounded-lg focus:ring-deep-pink focus:border-deep-pink transition-colors duration-200 text-base`} placeholder="Your full name" required disabled={isSubmitting} autoFocus data-id="element-8116" /> : <div className={`w-full px-4 py-4 border ${themeClasses.input} rounded-lg cursor-pointer hover:border-deep-pink/50 transition-colors duration-200 min-h-[56px] flex items-center`} data-id="element-8117">
                              {name || <span className={darkMode ? 'text-gray-500' : 'text-gray-600'} data-id="element-8118">
                                  Not specified
                                </span>}
                            </div>}
                        </div>
                      </div>
                      {/* Email */}
                      <div data-id="element-8119">
                        <label htmlFor="email" className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-8120">
                          Email Address
                        </label>
                        <div className="relative" onClick={() => !isEditingEmail && startEditing('email')} data-id="element-8121">
                          {isEditingEmail ? <input type="email" id="email" value={email} onChange={e => setEmail(e.target.value)} className={`w-full px-4 py-4 border ${themeClasses.inputActive} rounded-lg focus:ring-deep-pink focus:border-deep-pink transition-colors duration-200 text-base`} placeholder="you@example.com" required disabled={isSubmitting} autoFocus data-id="element-8122" /> : <div className={`w-full px-4 py-4 border ${themeClasses.input} rounded-lg cursor-pointer hover:border-deep-pink/50 transition-colors duration-200 min-h-[56px] flex items-center`} data-id="element-8123">
                              {email || <span className={darkMode ? 'text-gray-500' : 'text-gray-600'} data-id="element-8124">
                                  Not specified
                                </span>}
                            </div>}
                        </div>
                      </div>
                      {/* Phone */}
                      <div data-id="element-8125">
                        <label htmlFor="phone" className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-8126">
                          Phone Number
                        </label>
                        <div className="relative" onClick={() => !isEditingPhone && startEditing('phone')} data-id="element-8127">
                          {isEditingPhone ? <input type="tel" id="phone" value={phone} onChange={e => setPhone(e.target.value)} className={`w-full px-4 py-4 border ${themeClasses.inputActive} rounded-lg focus:ring-deep-pink focus:border-deep-pink transition-colors duration-200 text-base`} placeholder="Your phone number" disabled={isSubmitting} autoFocus data-id="element-8128" /> : <div className={`w-full px-4 py-4 border ${themeClasses.input} rounded-lg cursor-pointer hover:border-deep-pink/50 transition-colors duration-200 min-h-[56px] flex items-center`} data-id="element-8129">
                              {phone || <span className={darkMode ? 'text-gray-500' : 'text-gray-600'} data-id="element-8130">
                                  Not specified
                                </span>}
                            </div>}
                        </div>
                      </div>
                    </div>
                    {/* Save and Cancel Buttons - improved touch targets */}
                    <div className="mt-6 flex flex-col sm:flex-row gap-3" data-id="element-8131">
                      <button type="submit" disabled={isSubmitting || !dirtyFields.contact} className={`${themeClasses.buttonPrimary} font-medium py-4 px-6 rounded-lg transition-colors flex items-center justify-center ${!dirtyFields.contact ? 'opacity-50 cursor-not-allowed' : 'shadow-md hover:shadow-lg'}`} style={{
                      minHeight: '56px'
                    }} data-id="element-8132">
                        {isSubmitting ? <>
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-8133">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-8134"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-8135"></path>
                            </svg>
                            Saving...
                          </> : <>
                            <SaveIcon className="h-5 w-5 mr-2" data-id="element-8136" />
                            Save Changes
                          </>}
                      </button>
                      {isEditing && <button type="button" onClick={cancelEdits} disabled={isSubmitting} className={`${themeClasses.buttonSecondary} font-medium py-4 px-6 rounded-lg transition-colors flex items-center justify-center`} style={{
                      minHeight: '56px'
                    }} data-id="element-8137">
                          <XIcon className="h-5 w-5 mr-2" data-id="element-8138" />
                          Cancel
                        </button>}
                    </div>
                  </form>
                </div>
              </section>
              {/* Notification Settings Section - improved mobile toggles */}
              <section id="notifications" className="mb-10" data-id="element-8139">
                <div className={`${themeClasses.card} backdrop-blur-sm rounded-xl p-6`} data-id="element-8140">
                  <h2 className={`text-xl font-serif font-bold ${themeClasses.text} mb-6 border-b ${themeClasses.border} pb-2 flex items-center justify-between`} data-id="element-8141">
                    <div className="flex items-center" data-id="element-8142">
                      <BellIcon className="h-6 w-6 mr-2 text-deep-pink" data-id="element-8143" />
                      Notification Settings
                    </div>
                    {dirtyFields.notifications && <span className="h-2 w-2 rounded-full bg-deep-pink" data-id="element-8144"></span>}
                  </h2>
                  <div className="space-y-5" data-id="element-8145">
                    <div className={`p-5 rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-100 border border-gray-300'}`} data-id="element-8146">
                      <Toggle id="email-notifications" label="Email Notifications" checked={emailNotifications} onChange={setEmailNotifications} data-id="element-8147" />
                      <p className={`mt-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-700'}`} data-id="element-8148">
                        Receive notifications about lesson reminders, schedule
                        changes, and teacher messages via email.
                      </p>
                    </div>
                    <div className={`p-5 rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-100 border border-gray-300'}`} data-id="element-8149">
                      <Toggle id="sms-notifications" label="SMS Notifications" checked={smsNotifications} onChange={setSmsNotifications} data-id="element-8150" />
                      <p className={`mt-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-700'}`} data-id="element-8151">
                        Receive urgent notifications like last-minute
                        cancellations via SMS to your phone.
                      </p>
                    </div>
                    <div className={`p-5 rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-100 border border-gray-300'}`} data-id="element-8152">
                      <Toggle id="marketing-emails" label="Marketing Emails" checked={marketingEmails} onChange={setMarketingEmails} data-id="element-8153" />
                      <p className={`mt-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-700'}`} data-id="element-8154">
                        Receive updates about new features, learning resources,
                        and promotional offers.
                      </p>
                    </div>
                  </div>
                  <div className="mt-6" data-id="element-8155">
                    <button type="button" onClick={handleNotificationSubmit} disabled={isSubmitting || !dirtyFields.notifications} className={`${themeClasses.buttonPrimary} font-medium py-4 px-6 rounded-lg transition-colors flex items-center justify-center w-full sm:w-auto ${!dirtyFields.notifications ? 'opacity-50 cursor-not-allowed' : 'shadow-md hover:shadow-lg'}`} style={{
                    minHeight: '56px'
                  }} data-id="element-8156">
                      {isSubmitting ? <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-8157">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-8158"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-8159"></path>
                          </svg>
                          Saving...
                        </> : <>
                          <SaveIcon className="h-5 w-5 mr-2" data-id="element-8160" />
                          Save Notification Preferences
                        </>}
                    </button>
                  </div>
                </div>
              </section>
              {/* Security Settings Section */}
              <section id="security" className="mb-10" data-id="element-8161">
                <div className={`${themeClasses.card} backdrop-blur-sm rounded-xl p-6`} data-id="element-8162">
                  <h2 className={`text-xl font-serif font-bold ${themeClasses.text} mb-6 border-b ${themeClasses.border} pb-2 flex items-center`} data-id="element-8163">
                    <ShieldIcon className="h-6 w-6 mr-2 text-deep-pink" data-id="element-8164" />
                    Security
                  </h2>
                  <div className="space-y-6" data-id="element-8165">
                    <div data-id="element-8166">
                      <h3 className={`text-lg font-medium ${themeClasses.text} mb-2`} data-id="element-8167">
                        Password
                      </h3>
                      <p className={`${themeClasses.subtext} mb-4`} data-id="element-8168">
                        It's a good idea to use a strong password that you don't
                        use elsewhere.
                      </p>
                      <button type="button" className={`${themeClasses.buttonSecondary} font-medium py-2 px-4 rounded-lg transition-colors`} data-id="element-8169">
                        Change Password
                      </button>
                    </div>
                    <div className={`border-t ${themeClasses.border} pt-6`} data-id="element-8170">
                      <h3 className={`text-lg font-medium ${themeClasses.text} mb-2`} data-id="element-8171">
                        Two-Factor Authentication
                      </h3>
                      <p className={`${themeClasses.subtext} mb-4`} data-id="element-8172">
                        Add an extra layer of security to your account by
                        enabling two-factor authentication.
                      </p>
                      <button type="button" className={`${themeClasses.buttonSecondary} font-medium py-2 px-4 rounded-lg transition-colors`} data-id="element-8173">
                        Enable Two-Factor Authentication
                      </button>
                    </div>
                    <div className={`border-t ${themeClasses.border} pt-6`} data-id="element-8174">
                      <h3 className={`text-lg font-medium ${themeClasses.text} mb-2 text-red-500`} data-id="element-8175">
                        Delete Account
                      </h3>
                      <p className={`${themeClasses.subtext} mb-4`} data-id="element-8176">
                        Permanently delete your account and all associated data.
                        This action cannot be undone.
                      </p>
                      <button type="button" onClick={() => setIsDeleteAccountModalOpen(true)} className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors" data-id="element-8177">
                        Delete My Account
                      </button>
                    </div>
                  </div>
                </div>
              </section>
              {/* Preferences Section */}
              <section id="preferences" className="mb-10" data-id="element-8178">
                <div className={`${themeClasses.card} backdrop-blur-sm rounded-xl p-6`} data-id="element-8179">
                  <h2 className={`text-xl font-serif font-bold ${themeClasses.text} mb-6 border-b ${themeClasses.border} pb-2 flex items-center`} data-id="element-8180">
                    <PaletteIcon className="h-6 w-6 mr-2 text-deep-pink" data-id="element-8181" />
                    Preferences
                  </h2>
                  <div className="space-y-6" data-id="element-8182">
                    <div data-id="element-8183">
                      <h3 className={`text-lg font-medium ${themeClasses.text} mb-2`} data-id="element-8184">
                        Theme
                      </h3>
                      <p className={`${themeClasses.subtext} mb-4`} data-id="element-8185">
                        Choose between light and dark theme for your dashboard.
                      </p>
                      <div className="flex flex-wrap gap-3" data-id="element-8186">
                        <button type="button" onClick={() => {
                        setDarkMode(false);
                        localStorage.setItem('studentDashboardTheme', 'light');
                        // Dispatch event for other components
                        window.dispatchEvent(new CustomEvent('themeChange', {
                          detail: {
                            darkMode: false
                          }
                        }));
                      }} className={`px-4 py-2 rounded-lg flex items-center ${!darkMode ? 'bg-deep-pink text-white' : 'bg-gray-200 text-gray-800'}`} data-id="element-8187">
                          <SunIcon className="h-5 w-5 mr-2" data-id="element-8188" />
                          Light
                        </button>
                        <button type="button" onClick={() => {
                        setDarkMode(true);
                        localStorage.setItem('studentDashboardTheme', 'dark');
                        // Dispatch event for other components
                        window.dispatchEvent(new CustomEvent('themeChange', {
                          detail: {
                            darkMode: true
                          }
                        }));
                      }} className={`px-4 py-2 rounded-lg flex items-center ${darkMode ? 'bg-deep-pink text-white' : 'bg-gray-200 text-gray-800'}`} data-id="element-8189">
                          <MoonIcon className="h-5 w-5 mr-2" data-id="element-8190" />
                          Dark
                        </button>
                      </div>
                    </div>
                    <div className={`border-t ${themeClasses.border} pt-6`} data-id="element-8191">
                      <h3 className={`text-lg font-medium ${themeClasses.text} mb-2`} data-id="element-8192">
                        Language
                      </h3>
                      <p className={`${themeClasses.subtext} mb-4`} data-id="element-8193">
                        Select your preferred language for the dashboard
                        interface.
                      </p>
                      <select className={`px-4 py-2 ${themeClasses.input} rounded-lg focus:ring-deep-pink focus:border-deep-pink`} data-id="element-8194">
                        <option value="en" data-id="element-8195">English</option>
                        <option value="es" data-id="element-8196">Español</option>
                        <option value="fr" data-id="element-8197">Français</option>
                        <option value="de" data-id="element-8198">Deutsch</option>
                      </select>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-8199" />
      {/* Delete Account Modal */}
      {isDeleteAccountModalOpen && <DeleteAccountModal isOpen={isDeleteAccountModalOpen} onClose={() => setIsDeleteAccountModalOpen(false)} data-id="element-8200" />}
    </div>;
};